// Dashboard.js - CORRECT VERSION WITH QUERY PARAMETERS
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { IoFilterSharp } from "react-icons/io5";
import { MdOutlineDelete, MdOutlineModeEdit, MdDoneOutline } from "react-icons/md";
import "./Dashboard.css";
import { authFetch } from "../utils/authFetch";

function Dashboard({ storedResults = [], setStoredResults, fetchStoredUrls, userPlan }) {
  const [lockModalVisible, setLockModalVisible] = useState(false);
  const [expandedResultIndex, setExpandedResultIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const hourlyLocked = userPlan !== 'Active Job Seeker/ Hourly Search Plan';

  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalUrl, setModalUrl] = useState('');

  const [editIndex, setEditIndex] = useState(null);

  const showModal = (message, url = '') => {
    setModalMessage(message);
    setModalUrl(url);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalMessage('');
    setModalVisible(false);
  };

  function CompanyLogo({ result }) {
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [attempted, setAttempted] = useState(false);
    const hostname = (() => { try { return result.url ? new URL(result.url).hostname : null; } catch { return null; } })();
    const companyName = result.company_name || "Unknown Company";
    const formattedCompanyName = companyName.charAt(0).toUpperCase() + companyName.slice(1);
  
    useEffect(() => {
      if (!hostname) return;
  
      const img = new Image();
      const logoUrl = `https://logo.clearbit.com/${hostname}`;
      img.src = logoUrl;
  
      img.onload = () => {
        setLogoLoaded(true);
        setAttempted(true);
      };
  
      img.onerror = () => {
        setLogoLoaded(false);
        setAttempted(true);
      };
    }, [hostname]);
  
    return (
      <div className="tooltip-wrapper">
        {logoLoaded ? (
          <img
            src={`https://logo.clearbit.com/${hostname}`}
            alt={`${formattedCompanyName} Logo`}
            style={{
              width: "40px",
              height: "40px",
              objectFit: "contain",
              padding: "4px",
              border: "1px solid black",
              borderRadius: "8px"
            }}
          />
        ) : attempted ? (
          <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            {formattedCompanyName}
          </span>
        ) : (
          <div
            style={{
              width: "40px",
              height: "40px",
              padding: "4px",
              borderRadius: "8px",
              visibility: "hidden"
            }}
          />
        )}
        <span className="tooltip-text">{formattedCompanyName}</span>
      </div>
    );
  }

  const handleSearchManually = () => {
    if (modalUrl) {
      window.open(modalUrl, '_blank');
    }
    closeModal();
  };

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    recordId: null,
    keywordIndex: null,
  });

  useEffect(() => {
    const handleClick = () => {
      if (contextMenu.visible) {
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          recordId: null,
          keywordIndex: null
        });
      }
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [contextMenu.visible]);

  const userId = localStorage.getItem("userEmail");

  const [filterMatchType, setFilterMatchType] = useState("All");
  const [filterFrequencyFilter, setFilterFrequencyFilter] = useState("All");
  const [filterApplied, setFilterApplied] = useState("All");

  const [tempMatchType, setTempMatchType] = useState("All");
  const [tempFrequency, setTempFrequency] = useState("All");
  const [tempApplied, setTempApplied] = useState("All");

  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showFilterOptions && filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterOptions(false);
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, [showFilterOptions]);

  const filterRecord = (record) => {
    if (filterFrequencyFilter !== "All" && record.frequency !== filterFrequencyFilter) {
      return false;
    }
    if (filterMatchType !== "All") {
      const hasMatchType = record.keywords.some((kw) => kw.match_type === filterMatchType);
      if (!hasMatchType) return false;
    }
    if (filterApplied !== "All") {
      const hasApplied = record.keywords.some((kw) => {
        if (filterApplied === "Applied") return kw.mark_as_applied;
        if (filterApplied === "Not Applied") return !kw.mark_as_applied;
        return true;
      });
      if (!hasApplied) return false;
    }
    return true;
  };

  const sortedRecords = [...storedResults].sort((a, b) => b.timeRaw - a.timeRaw);
  const filteredRecords = sortedRecords.filter(filterRecord);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentResults = filteredRecords.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleDeleteSinglePill = async (rowIndex, pillIndex) => {
    const record = currentResults[rowIndex];
    if (!record) return;
  
    const pill = record.keywords[pillIndex];
    if (!pill || !pill.search_id) return;
  
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/delete_search_entry`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: userId,
            search_ids: [pill.search_id]
          })
        }
      );
      
      if (!response.ok) return;
      
      setStoredResults((prev) =>
        prev.map((r) => {
          if (r.search_id !== record.search_id) return r;
          const updatedKeywords = r.keywords.filter((_, i) => i !== pillIndex);
          if (updatedKeywords.length === 0) return null;
          return { ...r, keywords: updatedKeywords };
        }).filter(Boolean)
      );
      
      await fetchStoredUrls(userId);
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const handleFrequencyUpdate = async (record, newFrequency) => {
    try {
      await Promise.all(
        record.keywords.map(async (kw) => {
          const response = await authFetch(
          `${process.env.REACT_APP_API_BASE_URL}/update_frequency`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: userId,
                search_id: kw.search_id,
                frequency: newFrequency
              })
            }
          );
          if (!response.ok) {
            throw new Error("Failed to update frequency");
          }
        })
      );

      setStoredResults((prev) =>
        prev.map((r) =>
          r.search_id === record.search_id ? { ...r, frequency: newFrequency } : r
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getFrequencyBadgeClass = (frequency) => {
    const freqLower = String(frequency || "").toLowerCase();
    if (freqLower.includes("hour")) return "frequency-hourly";
    if (freqLower.includes("day")) return "frequency-daily";
    if (freqLower.includes("week")) return "frequency-weekly";
    if (freqLower.includes("month")) return "frequency-monthly";
    return "frequency-none";
  };

  const handleKeywordContextMenu = (e, record, keywordIndex) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      recordId: record.search_id,
      keywordIndex: keywordIndex
    });
  };

  const markAsApplied = async () => {
    const { recordId, keywordIndex } = contextMenu;
    const user_id = localStorage.getItem("userEmail");
  
    setStoredResults((prevResults) =>
      prevResults.map((r) => {
        if (r.search_id === recordId) {
          const updatedKeywords = r.keywords.map((kw, idx) =>
            idx === keywordIndex
              ? { ...kw, status: "applied", mark_as_applied: true, frequency: "None" }
              : kw
          );
          return { ...r, keywords: updatedKeywords };
        }
        return r;
      })
    );
  
    try {
      const targetRecord = storedResults.find((r) => r.search_id === recordId);
      const targetKeyword = targetRecord?.keywords[keywordIndex];
      const targetSearchId = targetKeyword?.search_id;
  
      if (!targetSearchId) throw new Error("Target search ID not found.");
  
      await authFetch(`${process.env.REACT_APP_API_BASE_URL}/update_tracking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          search_id: targetSearchId,
          mark_as_applied: true,
        }),
      });
  
      await authFetch(`${process.env.REACT_APP_API_BASE_URL}/update_frequency`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          search_id: targetSearchId,
          frequency: "None",
        }),
      });
    } catch (error) {
      console.error("Error marking as applied:", error);
    }
  
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  const undoMarkAsApplied = async () => {
    const { recordId, keywordIndex } = contextMenu;
    const user_id = localStorage.getItem("userEmail");
  
    try {
      const targetRecord = storedResults.find((r) => r.search_id === recordId);
      const targetKeyword = targetRecord?.keywords[keywordIndex];
      const targetSearchId = targetKeyword?.search_id;
  
      if (!targetSearchId || !targetRecord) {
        throw new Error("Missing target keyword or record.");
      }
  
      const parentFrequency = targetRecord.frequency || "None";
  
      await authFetch(`${process.env.REACT_APP_API_BASE_URL}/update_tracking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          search_id: targetSearchId,
          mark_as_applied: false,
        }),
      });
  
      await authFetch(`${process.env.REACT_APP_API_BASE_URL}/update_frequency`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id,
          search_id: targetSearchId,
          frequency: parentFrequency,
        }),
      });
  
      setStoredResults((prevResults) =>
        prevResults.map((r) => {
          if (r.search_id === recordId) {
            const updatedKeywords = r.keywords.map((kw, idx) =>
              idx === keywordIndex
                ? { ...kw, status: "", mark_as_applied: false, frequency: parentFrequency }
                : kw
            );
            return { ...r, keywords: updatedKeywords };
          }
          return r;
        })
      );
    } catch (error) {
      console.error("Error undoing applied:", error);
    }
  
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  const markAsIncorrect = () => {
    const { recordId, keywordIndex } = contextMenu;
    setStoredResults((prevResults) =>
      prevResults.map((r) => {
        if (r.search_id === recordId) {
          const updatedKeywords = r.keywords.map((kw, idx) =>
            idx === keywordIndex ? { ...kw, status: "incorrect" } : kw
          );
          return { ...r, keywords: updatedKeywords };
        }
        return r;
      })
    );
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  const markAsCorrect = () => {
    const { recordId, keywordIndex } = contextMenu;
    setStoredResults((prevResults) =>
      prevResults.map((r) => {
        if (r.search_id === recordId) {
          const updatedKeywords = r.keywords.map((kw, idx) =>
            idx === keywordIndex ? { ...kw, status: "correct" } : kw
          );
          return { ...r, keywords: updatedKeywords };
        }
        return r;
      })
    );
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  const handleFilterApply = () => {
    setFilterMatchType(tempMatchType);
    setFilterFrequencyFilter(tempFrequency);
    setFilterApplied(tempApplied);
    setShowFilterOptions(false);
  };

  const handleFilterClear = () => {
    setFilterMatchType("All");
    setFilterFrequencyFilter("All");
    setFilterApplied("All");
    setTempMatchType("All");
    setTempFrequency("All");
    setTempApplied("All");
    setShowFilterOptions(false);
  };

  const currentKeyword =
    contextMenu.visible && contextMenu.recordId
      ? storedResults.find((r) => r.search_id === contextMenu.recordId)?.keywords[contextMenu.keywordIndex]
      : null;

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-container">
        <h2>Click Green, Apply Now!</h2>

        <div className="filter-container">
          <IoFilterSharp
            className="filter-icon"
            onClick={(e) => {
              e.stopPropagation();
              setShowFilterOptions((prev) => !prev);
              setTempMatchType(filterMatchType);
              setTempFrequency(filterFrequencyFilter);
              setTempApplied(filterApplied);
            }}
            style={{ fontSize: "25px", cursor: "pointer" }}
          />

          {showFilterOptions && (
            <div ref={filterRef} className="filter-popover">
              <div style={{ marginBottom: "8px" }}>
                <label>Match Type:</label>
                <select
                  value={tempMatchType}
                  onChange={(e) => setTempMatchType(e.target.value)}
                  className="filter-select"
                  style={{ width: "100%", marginTop: "4px" }}
                >
                  <option value="All">All</option>
                  <option value="exact">exact</option>
                  <option value="partial">Partial</option>
                  <option value="not-found">Not Found</option>
                </select>
              </div>

              <div style={{ marginBottom: "8px" }}>
                <label>Frequency:</label>
                <select
                  value={tempFrequency}
                  onChange={(e) => setTempFrequency(e.target.value)}
                  className="filter-select"
                  style={{ width: "100%", marginTop: "4px" }}
                >
                  <option value="All">All</option>
                  <option value="None">None</option>
                  <option value="Hourly">Hourly</option>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>

              <div style={{ marginBottom: "8px" }}>
                <label>Applied:</label>
                <select
                  value={tempApplied}
                  onChange={(e) => setTempApplied(e.target.value)}
                  className="filter-select"
                  style={{ width: "100%", marginTop: "4px" }}
                >
                  <option value="All">All</option>
                  <option value="Applied">Applied</option>
                  <option value="Not Applied">Not Applied</option>
                </select>
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                <button
                  style={{
                    flex: 1,
                    background: "#4caf50",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "8px 0",
                    cursor: "pointer"
                  }}
                  onClick={handleFilterApply}
                >
                  Apply
                </button>

                <button
                  style={{
                    flex: 1,
                    background: "#ccc",
                    color: "#000",
                    border: "none",
                    borderRadius: "4px",
                    padding: "8px 0",
                    cursor: "pointer"
                  }}
                  onClick={handleFilterClear}
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        <table className="dashboard-table" style={{ marginTop: "30px" }}>
          <colgroup>
            <col style={{ width: "7%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "5%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Company</th>
              <th>Date & Time</th>
              <th>Keywords</th>
              <th>Frequency</th>
              <th>Runtime</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentResults.map((record, idx) => {
              const companyLink =
                record.base_url ||
                (() => {
                  try {
                    return new URL(record.url).origin;
                  } catch {
                    return record.url;
                  }
                })();

              if (!record.keywords || record.keywords.length === 0) {
                return null;
              }

              const globalIndex = idx;

              return (
                <React.Fragment key={record.search_id || idx}>
                  <tr
                    className={`
                      ${(record.frequency || "None") === "None" ? "row-none-frequency" : "row-other-frequency"} 
                      ${editIndex === globalIndex ? "row-editing" : ""}
                    `}
                  >
                    <td>
                      <a
                        href={companyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <CompanyLogo result={record} />
                      </a>
                    </td>

                    <td>
                      {record.timeFormatted && record.timeFormatted.includes(",") ? (
                        <>
                          {record.timeFormatted.split(",")[0]},<br />
                          {record.timeFormatted.split(",")[1].trim()}
                        </>
                      ) : (
                        record.timeFormatted || "N/A"
                      )}
                    </td>

                    <td>
                      <div className="keyword-pills-container">
                        {record.keywords.map((kw, i) => (
                          <span
                            key={i}
                            className={`keyword-pill ${
                              kw.mark_as_applied
                                ? "applied-pill"
                                : kw.match_type === "exact"
                                ? "green-pill"
                                : kw.match_type === "partial"
                                ? "orange-pill"
                                : "red-pill"
                            } ${kw.status === "complete" ? "completed" : ""}`}
                            onClick={e => {
                              if (kw.mark_as_applied) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                              }
                              if (
                                (kw.match_type === "exact" || kw.match_type === "partial") &&
                                kw.url
                              ) {
                                window.open(kw.url, "_blank");
                              } else {
                                showModal(
                                  `No match found for the keyword "${kw.keyword}".`,
                                  kw.url || record.url
                                );
                              }
                            }}
                            onContextMenu={(e) => handleKeywordContextMenu(e, record, i)}
                            style={{ cursor: kw.mark_as_applied ? "not-allowed" : "pointer" }}
                          >
                            {kw.keyword || "Unknown"}
                            {kw.mark_as_applied && (
                              <MdDoneOutline
                                style={{ marginLeft: "4px", verticalAlign: "middle" }}
                                title="Applied"
                              />
                            )}
                            {editIndex === globalIndex && (
                              <MdOutlineDelete
                                className="pill-delete-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSinglePill(globalIndex, i);
                                }}
                                style={{ marginLeft: "6px", cursor: "pointer" }}
                              />
                            )}
                          </span>
                        ))}
                      </div>
                    </td>

                    <td>
                      <select
                        value={record.frequency || "None"}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === "Hourly" && hourlyLocked) {
                            setLockModalVisible(true);
                          } else {
                            handleFrequencyUpdate(record, val);
                          }
                        }}
                        className={`frequency-status ${getFrequencyBadgeClass(record.frequency)}`}
                      >
                        <option value="None">None</option>
                        <option value="Hourly">
                          Hourly{hourlyLocked && " 🔒"}
                        </option>
                        <option value="Daily">Daily</option>
                      </select>
                    </td>

                    <td className="runtime-col">
                      <div>{record.runtime}</div>
                    </td>

                    <td className="actions-cell">
                      {editIndex === globalIndex ? (
                        <button className="btn-edit" onClick={() => setEditIndex(null)}>
                          <MdDoneOutline style={{ color: "black", fontSize: "25px" }} />
                        </button>
                      ) : (
                        <button className="btn-edit" onClick={() => setEditIndex(globalIndex)}>
                          <MdOutlineModeEdit style={{ color: "black", fontSize: "25px" }} />
                        </button>
                      )}
                    </td>
                  </tr>

                  {expandedResultIndex === globalIndex && (
                    <tr>
                      <td colSpan={6} className="expanded-section">
                        <div>
                          <h4>Detailed Information:</h4>
                          <p>
                            <strong>URL:</strong>{" "}
                            <a href={record.url} target="_blank" rel="noopener noreferrer">
                              {record.url}
                            </a>
                          </p>
                          <p>
                            <strong>Runtime:</strong> {record.runtime}
                          </p>
                          {record.match_status_found && (
                            <p style={{ fontWeight: "bold", color: "green" }}>
                              Job is found
                            </p>
                          )}
                          <h5>Keywords:</h5>
                          <ul>
                            {record.keywords.map((kw, i) => (
                              <li key={i}>
                                <strong>{kw.keyword}</strong>: {kw.match_type}
                                {kw.url && (
                                  <span>
                                    {" "}
                                    -{" "}
                                    <a href={kw.url} target="_blank" rel="noopener noreferrer">
                                      View
                                    </a>
                                  </span>
                                )}
                                {kw.status && (
                                  <span style={{ marginLeft: "8px", fontStyle: "italic" }}>
                                    [{kw.status}]
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        <div className="pagination-container">
          {currentPage > 1 && (
            <button onClick={() => setCurrentPage(currentPage - 1)} className="pagination-button">
              Previous
            </button>
          )}
          {Array.from({ length: Math.ceil(filteredRecords.length / ITEMS_PER_PAGE) }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`pagination-button ${currentPage === i + 1 ? "active" : ""}`}
            >
              {i + 1}
            </button>
          ))}
          {currentPage < Math.ceil(filteredRecords.length / ITEMS_PER_PAGE) && (
            <button onClick={() => setCurrentPage(currentPage + 1)} className="pagination-button">
              Next
            </button>
          )}
        </div>

        {modalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="modal-close-icon" onClick={closeModal}>
                ×
              </button>
              <p className="modal-message">{modalMessage}</p>
              <button className="modal-search-button" onClick={handleSearchManually}>
                Search Manually
              </button>
            </div>
          </div>
        )}

        {lockModalVisible && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="modal-close-icon"
                onClick={() => setLockModalVisible(false)}
              >
                ×
              </button>
              <p>
                Please subscribe to the{" "}
                <strong>Active Job Seeker / Hourly Search Plan</strong> to unlock hourly frequency.
              </p>
              <button
                className="modal-search-button"
                onClick={() => {
                  setLockModalVisible(false);
                  window.location.href = "/pricing";
                }}
              >
                View Plans
              </button>
            </div>
          </div>
        )}

        {contextMenu.visible &&
          ReactDOM.createPortal(
            <ul
              className="context-menu"
              style={{
                top: `${contextMenu.y}px`,
                left: `${contextMenu.x}px`
              }}
            >
              {currentKeyword && currentKeyword.mark_as_applied ? (
                <li onClick={undoMarkAsApplied}>Undo mark as applied</li>
              ) : (
                <li onClick={markAsApplied}>Mark as applied</li>
              )}
              <li onClick={markAsIncorrect}>Mark as incorrect result</li>
              <li onClick={markAsCorrect}>Mark as correct result</li>
            </ul>,
            document.body
          )}
      </div>
    </div>
  );
}

export default Dashboard;